import * as THREE from 'three';
import { SectionRect } from '../types';

interface DividerSpec {
  orientation: 'vertical' | 'horizontal';
  position: number;
  start: number;
  end: number;
}

/**
 * Analyze section rectangles and compute divider positions.
 * Dividers are placed at shared edges between adjacent sections.
 */
export function computeDividers(
  sections: SectionRect[],
  frameWidth: number,
): DividerSpec[] {
  const dividers: DividerSpec[] = [];
  const eps = 0.0001;

  for (let i = 0; i < sections.length; i++) {
    for (let j = i + 1; j < sections.length; j++) {
      const a = sections[i];
      const b = sections[j];

      // Check for vertical divider (sections side by side)
      const sharedX = findSharedEdge(
        a.x, a.x + a.width,
        b.x, b.x + b.width,
        eps,
      );
      if (sharedX !== null) {
        const overlapY = getOverlap(a.y, a.y + a.height, b.y, b.y + b.height, eps);
        if (overlapY) {
          dividers.push({
            orientation: 'vertical',
            position: sharedX,
            start: overlapY.start,
            end: overlapY.end,
          });
        }
      }

      // Check for horizontal divider (sections stacked)
      const sharedY = findSharedEdge(
        a.y, a.y + a.height,
        b.y, b.y + b.height,
        eps,
      );
      if (sharedY !== null) {
        const overlapX = getOverlap(a.x, a.x + a.width, b.x, b.x + b.width, eps);
        if (overlapX) {
          dividers.push({
            orientation: 'horizontal',
            position: sharedY,
            start: overlapX.start,
            end: overlapX.end,
          });
        }
      }
    }
  }

  return deduplicateDividers(dividers, eps);
}

function findSharedEdge(
  aMin: number, aMax: number,
  bMin: number, bMax: number,
  eps: number,
): number | null {
  // a's right edge touches b's left edge
  if (Math.abs(aMax - bMin) < eps) return aMax;
  // b's right edge touches a's left edge
  if (Math.abs(bMax - aMin) < eps) return bMax;
  return null;
}

function getOverlap(
  aMin: number, aMax: number,
  bMin: number, bMax: number,
  eps: number,
): { start: number; end: number } | null {
  const start = Math.max(aMin, bMin);
  const end = Math.min(aMax, bMax);
  if (end - start > eps) return { start, end };
  return null;
}

function deduplicateDividers(dividers: DividerSpec[], eps: number): DividerSpec[] {
  const result: DividerSpec[] = [];
  for (const d of dividers) {
    const existing = result.find(
      r => r.orientation === d.orientation &&
           Math.abs(r.position - d.position) < eps &&
           Math.abs(r.start - d.start) < eps &&
           Math.abs(r.end - d.end) < eps,
    );
    if (!existing) result.push(d);
  }
  return result;
}

/**
 * Create divider meshes from computed specs.
 */
export function createDividerMeshes(
  sections: SectionRect[],
  frameWidth: number,
  mullionWidth: number,
  mullionDepth: number,
  material: THREE.Material,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'dividers';

  const dividers = computeDividers(sections, frameWidth);

  for (const d of dividers) {
    if (d.orientation === 'vertical') {
      const length = d.end - d.start;
      const geo = new THREE.BoxGeometry(mullionWidth, length, mullionDepth);
      const mesh = new THREE.Mesh(geo, material);
      mesh.position.set(d.position, d.start + length / 2, 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    } else {
      const length = d.end - d.start;
      const geo = new THREE.BoxGeometry(length, mullionWidth, mullionDepth);
      const mesh = new THREE.Mesh(geo, material);
      mesh.position.set(d.start + length / 2, d.position, 0);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
  }

  return group;
}
